'use strict';

import MlbLinker from 'mlb-linker';
import GameUtils from './GameUtils';
import MilbButtonGroup from './MilbButtonGroup';
import ArgValidator from 'bam-arg-validator';
import { getDictFromContext } from './MlbGameUtils/helpers';

const CALLER_CODE_PROBABLE_PITCHERS_SECTION = 'probable-pitchersSection';

const CALLER_CODE_CLUB_SCHEDULE_SECTION = 'club-sked';
const CALLER_CODE_LEAGUE_SCHEDULE_SECTION = 'league-sked';

// TODO: Replace with CALLER_CODE_SCORES_* with MEDIAWALL
const CALLER_CODE_SCORES_MEDIAWALL_DESKTOP = 'mediawall-desktop';
const CALLER_CODE_SCORES_MEDIAWALL_DEVICE = 'mediawall-device';

const CALLER_CODE_SCORES_MINISB_DESKTOP = 'minisb-desktop';
const CALLER_CODE_SCORES_MINISB_DEVICE = 'minisb-device';

const CALLER_CODE_SCORES_SECTION_DESKTOP = 'scoresSection-desktop';
const CALLER_CODE_SCORES_SECTION_DEVICE = 'scoresSection-device';
const CALLER_CODE_SCORES_SECTION_FIRSTPITCH = 'scoresSection-firstpitch';

const CALLER_CODE_SCORES_MIG_DESKTOP = 'scoresMig-desktop';
const CALLER_CODE_SCORES_MIG_DEVICE = 'scoresMig-device';

const CODE_TENSE_BUTTONS = {
    'probable-pitchersSection': {
        future: ['preview', 'gameday', 'tickets'],
        present: ['gameday', 'audio', 'watchMilb'],
        past: ['wrap', 'watchMilb', 'vidhighlights']
    },
    'club-sked': {
        future: ['preview', 'watchMilb', 'gameday', 'tickets'],
        present: ['watchMilb', 'gameday', 'tickets', 'audioMilbTeam'],
        past: ['recap', 'boxscore']
    },
    'league-sked': {
        future: ['preview', 'watchMilb', 'gameday', 'tickets'],
        present: ['watchMilb', 'gameday', 'tickets', 'audioMilb'],
        past: ['recap', 'watchMilb', 'boxscore', 'tickets']
    },
    'mediawall-desktop': {
        future: ['preview', 'gameday', 'tickets'],
        present: ['gameday', 'watchMilb', 'tickets'],
        past: ['recap', 'boxscore', 'watchMilb']
    },
    'mediawall-device': {
        future: ['preview', 'gameday', 'tickets'],
        present: ['gameday', 'tickets'],
        past: ['recap', 'boxscore']
    },
    'minisb-desktop': {
        future: ['preview', 'watchMilb', 'tickets'],
        present: ['gameday', 'watchMilb'],
        past: ['wrap', 'boxscore']
    },
    'minisb-device': {
        future: ['preview', 'tickets'],
        present: ['watchMilb', 'gameday'],
        past: ['wrap', 'boxscore']
    },
    'scoresSection-desktop': {
        future: ['preview', 'gameday', 'watchMilb', 'tickets'],
        present: ['gameday', 'audioMilb', 'watchMilb', 'tickets'],
        past: ['wrap', 'boxscore', 'watchMilb']
    },
    'scoresSection-device': {
        future: ['preview', 'gameday', 'tickets'],
        present: ['gameday', 'tickets'],
        past: ['wrap', 'boxscore']
    },
    'scoresSection-firstpitch': {
        future: ['preview', 'gameday', 'tickets'],
        present: ['gameday', 'tickets'],
        past: ['wrap', 'boxscore']
    },
    'scoresMig-desktop': {
        future: ['preview', 'tickets', 'scheduleMilb'],
        present: ['watchMilb', 'gameday', 'audioMilbTeam', 'scheduleMilb'],
        past: ['wrap', 'boxscore', 'watchMilb', 'scheduleMilb']
    },
    'scoresMig-device': {
        future: ['preview', 'tickets', 'scheduleMilb'],
        present: ['watchMilb', 'gameday', 'audioMilbTeam', 'scheduleMilb'],
        past: ['wrap', 'boxscore', 'watchMilb', 'scheduleMilb']
    }
};

/**
 * @class MilbGameUtils
 */
class MilbGameUtils extends GameUtils {

    /**
     * This class can be instantiated with a null or undefined windowOrReq, in which case the constructor assumes
     * that we are in a browser and our windowOrReq is the browser window object.  Thus, the following calls to this
     * constructor all result in the browser window object (not the controller req object) being used to initialize:
     *
     * - new MilbGameUtils(window, someCode);
     * - new MilbGameUtils(null, someCode);
     * - new MilbGameUtils(undefined, someCode);
     *
     * @param {object} ctx Either a BANE browser window or a BANE-controller "req"; optional (see above)
     * @param {string} callerCode Which UI element is instantiating this MlbGameUtils; accepted values are the
     *   CALLER_CODE_* instance constants in this class. Required.
     */
    constructor(ctx, callerCode) {

        let context = ctx;
        if (!context) {
            if (!(!window)) {
                context = window;
            } else {
                throw new Error('windowOrReq was null or undefined, but no window object was found.');
            }
        }

        // Note: throws Error if ctx is not valid:
        let bamLinker = new MlbLinker(context, { isMilb: true });
        let dictionaryData = getDictFromContext(context);

        // Validate the callerCode
        ArgValidator.validateAllowed(callerCode, [
            CALLER_CODE_PROBABLE_PITCHERS_SECTION,
            CALLER_CODE_CLUB_SCHEDULE_SECTION,
            CALLER_CODE_LEAGUE_SCHEDULE_SECTION,
            CALLER_CODE_SCORES_MEDIAWALL_DESKTOP,
            CALLER_CODE_SCORES_MEDIAWALL_DEVICE,
            CALLER_CODE_SCORES_MINISB_DESKTOP,
            CALLER_CODE_SCORES_MINISB_DEVICE,
            CALLER_CODE_SCORES_SECTION_DESKTOP,
            CALLER_CODE_SCORES_SECTION_DEVICE,
            CALLER_CODE_SCORES_SECTION_FIRSTPITCH,
            CALLER_CODE_SCORES_MIG_DESKTOP,
            CALLER_CODE_SCORES_MIG_DEVICE
        ]);

        super({
            linker: bamLinker,
            callerCode: callerCode,
            dictionaryData,
            moment: context.milbMoment,
            codeTenseButtons: CODE_TENSE_BUTTONS,
            buttonGroup: MilbButtonGroup
        });

    }

    /**
     * Indicates the instantiator of the MlbGameUtils is the probablePitchers section
     * @type {string}
     */
    static get CALLER_CODE_PROBABLE_PITCHERS_SECTION() {
        return CALLER_CODE_PROBABLE_PITCHERS_SECTION;
    }

    /**
     * Indicates the instantiator of the MlbGameUtils is the club-schedule section
     * @type {string}
     */
    static get CALLER_CODE_CLUB_SCHEDULE_SECTION() {
        return CALLER_CODE_CLUB_SCHEDULE_SECTION;
    }

    /**
     * Indicates the instantiator of the MlbGameUtils is the league-schedule section
     * @type {string}
     */
    static get CALLER_CODE_LEAGUE_SCHEDULE_SECTION() {
        return CALLER_CODE_LEAGUE_SCHEDULE_SECTION;
    }

    /**
     * Indicates the instantiator of the MLBGameUtils for mediawall scoreboards
     * @returns {string}
     * @todo: Rename to mediawall
     */
    static get CALLER_CODE_SCORES_MEDIAWALL_DESKTOP() {
        return CALLER_CODE_SCORES_MEDIAWALL_DESKTOP;
    }

    /**
     * Indicates the instantiator of the MLBGameUtils for mediawall scoreboards
     * @returns {string}
     * @todo: Rename to mediawall
     */
    static get CALLER_CODE_SCORES_MEDIAWALL_DEVICE() {
        return CALLER_CODE_SCORES_MEDIAWALL_DEVICE;
    }

    /**
     * Indicates the instantiator of the MLBGameUtils for the miniscoreboard in a wired environment
     * @returns {string}
     */
    static get CALLER_CODE_SCORES_MINISB_DESKTOP() {
        return CALLER_CODE_SCORES_MINISB_DESKTOP;
    }

    /**
     * Indicates the instantiator of the MLBGameUtils for the miniscoreboard in a tablet or phone environment
     * @returns {string}
     */
    static get CALLER_CODE_SCORES_MINISB_DEVICE() {
        return CALLER_CODE_SCORES_MINISB_DEVICE;
    }

    /**
     * Indicates the instantiator of the MLBGameUtils for the responsive scoreboards in a wired environment
     * @returns {string}
     */
    static get CALLER_CODE_SCORES_SECTION_DESKTOP() {
        return CALLER_CODE_SCORES_SECTION_DESKTOP;
    }

    /**
     * Indicates the instantiator of the MLBGameUtils for the responsive scoreboards in a tablet or phone environment
     * @returns {string}
     */
    static get CALLER_CODE_SCORES_SECTION_DEVICE() {
        return CALLER_CODE_SCORES_SECTION_DEVICE;
    }

    /**
     * Indicates the instantiator of the MLBGameUtils for the responsive scoreboards in firstpitch webview environment
     * @returns {string}
     */
    static get CALLER_CODE_SCORES_SECTION_FIRSTPITCH() {
        return CALLER_CODE_SCORES_SECTION_FIRSTPITCH;
    }

    /**
     * Indicates the instantiator of the MiLBGameUtils for the MIG in a wired environment
     * @returns {string}
     */
    static get CALLER_CODE_SCORES_MIG_DESKTOP() {
        return CALLER_CODE_SCORES_MIG_DESKTOP;
    }

    /**
     * Indicates the instantiator of the MiLBGameUtils for the MIG in a tablet or phone environment
     * @returns {string}
     */
    static get CALLER_CODE_SCORES_MIG_DEVICE() {
        return CALLER_CODE_SCORES_MIG_DEVICE;
    }

}

export default MilbGameUtils;
